import { isDebugMode } from "@utils/envs";
import * as Application from "expo-application";
import * as Updates from "expo-updates";
import { Platform } from "react-native";
import { theme } from "@theme";
import ExpoConstants from "expo-constants";

const EXPO_PUBLIC_FIREBASE_CONFIG =
  process.env.EXPO_PUBLIC_FIREBASE_CONFIG || "";
const EXPO_PUBLIC_WEB_CLIENT_ID = process.env.EXPO_PUBLIC_WEB_CLIENT_ID || "";
const EXPO_PUBLIC_IOS_CLIENT_ID = process.env.EXPO_PUBLIC_IOS_CLIENT_ID || "";
const EXPO_PUBLIC_IOS_SCHEME = process.env.EXPO_PUBLIC_IOS_SCHEME || "";
const REVENUECAT_API_KEY_ANDROID =
  process.env.EXPO_PUBLIC_REVENUECAT_API_KEY_ANDROID || "";
const REVENUECAT_API_KEY_IOS =
  process.env.EXPO_PUBLIC_REVENUECAT_API_KEY_IOS || "";
const REVENUECAT_API_KEY_WEB =
  process.env.EXPO_PUBLIC_REVENUECAT_API_KEY_WEB || "";
const SENTRY_DSN = process.env.EXPO_PUBLIC_SENTRY_DSN || "";
const SENTRY_RELEASE = process.env.EXPO_PUBLIC_SENTRY_RELEASE || "";
const LISSEN_ENV = process.env.EXPO_PUBLIC_LISSEN_ENV || "";
const EAS_PROJECT_ID = process.env.EXPO_PUBLIC_EAS_PROJECT_ID || "";
const EXPO_PUBLIC_API_URL = process.env.EXPO_PUBLIC_API_URL || "";
const ADMOB_IOS_UNIT_ID = process.env.EXPO_PUBLIC_ADMOB_IOS_UNIT_ID || "";
const ADMOB_ANDROID_UNIT_ID =
  process.env.EXPO_PUBLIC_ADMOB_ANDROID_UNIT_ID || "";
const EXPO_PUBLIC_VAST_IOS = process.env.EXPO_PUBLIC_VAST_IOS || "";
const EXPO_PUBLIC_VAST_ANDROID = process.env.EXPO_PUBLIC_VAST_ANDROID || "";
const EXPO_PUBLIC_STRIPE_MERCHANT_IDENTIFIER =
  process.env.EXPO_PUBLIC_STRIPE_MERCHANT_IDENTIFIER || "";
const EXPO_PUBLIC_INTERCOM_APP_ID =
  process.env.EXPO_PUBLIC_INTERCOM_APP_ID ||
  process.env.INTERCOM_APP_ID ||
  "";

const VAST = Platform.select({
  ios: EXPO_PUBLIC_VAST_IOS,
  android: EXPO_PUBLIC_VAST_ANDROID,
});

const ADMOB_UNIT_ID = Platform.select({
  ios: ADMOB_IOS_UNIT_ID,
  android: ADMOB_ANDROID_UNIT_ID,
});

const fbc = JSON.parse(EXPO_PUBLIC_FIREBASE_CONFIG || "{}");

const projectId = fbc?.projectId as string;
const isPreviewEnvironment =
  LISSEN_ENV === "preview" || LISSEN_ENV.startsWith("pr-");
const sentryEnvironment = isPreviewEnvironment ? "preview" : projectId;

if (!projectId) {
  throw Error(
    `There is no project id, please check that '${EXPO_PUBLIC_FIREBASE_CONFIG} is valid`,
  );
}

// NOTE
// when using `signUrl` with `CDN_URL`, call this at the call site and
// not in places that will be called immediately the app is opened
const CDN_URL = `https://cdn.${projectId}.lissen.live`;
const PROD_CDN_URL = `https://cdn.lissenprod.lissen.live`;

export default {
  PROJECT_ID: projectId,
  CDN_URL,
  PROD_CDN_URL,
  WEB_CLIENT_ID: EXPO_PUBLIC_WEB_CLIENT_ID,
  IOS_CLIENT_ID: EXPO_PUBLIC_IOS_CLIENT_ID,
  API_URL: EXPO_PUBLIC_API_URL,
  // In the initialisation of the app, sign url won't work as the query client hasn't loaded this
  // we need to call this function when we need it
  TERMS_URL: `${CDN_URL}/terms_and_conditions.pdf`,
  // lissen.live/legals/* died in the lissen.com domain move.
  PRIVACY_URL: `https://www.lissen.com/privacy-policy`,
  EXPO_PUBLIC_IOS_SCHEME,
  UI: {
    //TODO: make this dynamic
    tabBarHeight: Platform.OS === "ios" ? 90 : 70,
    barMusicPlayerHeight: 76,
    topBarHeight: 40 + theme.spacing.s2,
    zIndex: {
      tips: 9999,
      modal: 10000, // Always 1 above tips
    },
  },
  SENTRY_DSN,
  SENTRY_ENVIRONMENT: sentryEnvironment,
  SENTRY_RELEASE,
  APP_VERSION:
    Application.nativeApplicationVersion ||
    ExpoConstants.expoConfig?.version ||
    "",
  APP_BUILD_NUMBER: Application.nativeBuildVersion,
  UPDATES: {
    CHANNEL: Updates.channel ?? "",
    CREATED_AT: Updates.createdAt?.toISOString() ?? "",
    IS_EMBEDDED_LAUNCH: Updates.isEmbeddedLaunch ?? "",
    RUNTIME_VERSION: Updates.runtimeVersion ?? "",
    ID: Updates.updateId ?? "",
  },
  REVENUECAT_API_KEY: {
    android: REVENUECAT_API_KEY_ANDROID,
    ios: REVENUECAT_API_KEY_IOS,
    web: REVENUECAT_API_KEY_WEB,
  },
  extra: {
    eas: {
      projectId: EAS_PROJECT_ID,
    },
  },
  user: {
    // Change this to hardcode the countryCode.
    countryCode: isDebugMode() ? null : null,
  },
  ADMOB: ADMOB_UNIT_ID || "",
  VAST: VAST,
  STRIPE_MERCHANT_IDENTIFIER: EXPO_PUBLIC_STRIPE_MERCHANT_IDENTIFIER,
  RUDDERSTACK: {
    WRITE_KEY: process.env.EXPO_PUBLIC_RUDDERSTACK_WRITE_KEY || "",
    DATA_PLANE_URL: process.env.EXPO_PUBLIC_RUDDERSTACK_DATA_PLANE_URL || "",
  },
  CUSTOMERIO: {
    API_KEY: process.env.EXPO_PUBLIC_CUSTOMERIO_API_KEY || "",
    SITE_ID: process.env.EXPO_PUBLIC_CUSTOMERIO_SITE_ID || "",
  },
  INTERCOM: {
    APP_ID: EXPO_PUBLIC_INTERCOM_APP_ID,
  },
  BRANCH: {
    API_KEY:
      process.env.EXPO_PUBLIC_PROJECT_ID === "lissenprod"
        ? process.env.EXPO_PUBLIC_BRANCH_LIVE_KEY || ""
        : process.env.EXPO_PUBLIC_BRANCH_TEST_KEY || "",
  },
  /**
   * Audio engine used by `songManager` / `TrackManager`.
   *
   * Phase 4+: RNTP removed; expo-audio is now the only engine.
   */
  PLAYBACK_ENGINE: "expo-audio" as const,
};
